import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Button,
  Card,
  Container,
  FormControlLabel,
  Stack,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
  Typography,
} from '@mui/material';
import { HeaderBreadcrumbs, Iconify, Page, Scrollbar } from '@/components';
import DialogSimple from '@/components/dialog/DialogSimple';
import { SkeletonLoadingTable } from '@/components/skeleton';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
} from '@/components/table';
import { PATH_USER } from '@/features/app';
import { useGetAccess } from '@/hooks/useAccess';
import useSettings from '@/hooks/useSettings';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable from '@/hooks/useTable';
import {
  createScheduledJob,
  deleteScheduledJob,
  getScheduledJobs,
  runScheduledJobWithLatestHeaders,
  updateScheduledJob,
} from '../api/scheduled-jobs';
import {
  ScheduledJob,
  ScheduledJobListFilters,
  ScheduledJobPayload,
} from '../types/scheduled-jobs';
import {
  ScheduledJobDetailsDrawer,
  ScheduledJobFormDrawer,
  ScheduledJobsTableRow,
  ScheduledJobsTableToolbar,
} from '../components';

const TABLE_HEAD = [
  { id: 'name', label: 'Name', align: 'left', minWidth: 220 },
  { id: 'enabled', label: 'Enabled', align: 'left' },
  {
    id: 'cronExpression',
    label: 'Cron expression',
    align: 'left',
    minWidth: 150,
  },
  { id: 'timeZone', label: 'Timezone', align: 'left', minWidth: 150 },
  { id: 'executionType', label: 'Execution type', align: 'left' },
  { id: 'lastStatus', label: 'Last status', align: 'left' },
  { id: 'lastRunAt', label: 'Last run at', align: 'left', minWidth: 170 },
  { id: 'lastError', label: 'Last error', align: 'left', minWidth: 220 },
];

const DEFAULT_FILTERS: ScheduledJobListFilters = {
  search: '',
  enabled: 'all',
  executionType: '',
};

const isFiltersApplied = (filters: ScheduledJobListFilters) =>
  filters.search !== '' ||
  filters.enabled !== 'all' ||
  filters.executionType !== '';

export default function ScheduledJobsList() {
  const { themeStretch } = useSettings();
  const permissions = useGetAccess('settings');
  const { errorMsg, successMsg } = useSnackbarMsg();
  const queryClient = useQueryClient();
  const table = useTable({ defaultOrderBy: 'name', defaultRowsPerPage: 25 });

  const [filters, setFilters] =
    useState<ScheduledJobListFilters>(DEFAULT_FILTERS);
  const [formOpen, setFormOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [focusRuns, setFocusRuns] = useState(false);
  const [currentJob, setCurrentJob] = useState<ScheduledJob | null>(null);
  const [jobToRun, setJobToRun] = useState<ScheduledJob | null>(null);
  const [jobToDelete, setJobToDelete] = useState<ScheduledJob | null>(null);

  const jobsQueryKey = [
    'scheduledJobs',
    table.page,
    table.rowsPerPage,
    filters.enabled,
    filters.executionType,
    filters.search,
  ];

  const { data, isLoading } = useQuery({
    queryKey: jobsQueryKey,
    queryFn: () =>
      getScheduledJobs({
        page: table.page + 1,
        limit: table.rowsPerPage,
        enabled:
          filters.enabled === 'all' ? undefined : filters.enabled === 'enabled',
        executionType: filters.executionType || undefined,
        search: filters.search || undefined,
      }),
    keepPreviousData: true,
    onError: (error) => {
      errorMsg(error, 'Unable to load scheduled jobs');
    },
  });

  const createJobMutation = useMutation({
    mutationFn: (payload: ScheduledJobPayload) => createScheduledJob(payload),
    onSuccess: () => {
      successMsg('Scheduled job created successfully');
      queryClient.invalidateQueries(['scheduledJobs']);
      setFormOpen(false);
      setCurrentJob(null);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to create scheduled job');
    },
  });

  const updateJobMutation = useMutation({
    mutationFn: ({
      id,
      payload,
    }: {
      id: ScheduledJob['id'];
      payload: Partial<ScheduledJobPayload>;
    }) => updateScheduledJob(id, payload),
    onSuccess: (updatedJob) => {
      successMsg('Scheduled job updated successfully');
      queryClient.invalidateQueries(['scheduledJobs']);
      queryClient.invalidateQueries(['scheduledJobRuns', updatedJob.id]);
      setFormOpen(false);
      setCurrentJob((previous) =>
        previous && previous.id === updatedJob.id ? updatedJob : previous
      );
    },
    onError: (error) => {
      errorMsg(error, 'Unable to update scheduled job');
    },
  });

  const toggleEnabledMutation = useMutation({
    mutationFn: ({ job, enabled }: { job: ScheduledJob; enabled: boolean }) =>
      updateScheduledJob(job.id, { enabled }),
    onSuccess: (updatedJob) => {
      successMsg(
        `Scheduled job ${updatedJob.enabled ? 'enabled' : 'disabled'}`
      );
      queryClient.invalidateQueries(['scheduledJobs']);
      setCurrentJob((previous) =>
        previous && previous.id === updatedJob.id ? updatedJob : previous
      );
    },
    onError: (error) => {
      errorMsg(error, 'Unable to update scheduled job status');
    },
  });

  const runJobMutation = useMutation({
    mutationFn: (job: ScheduledJob) => runScheduledJobWithLatestHeaders(job),
    onSuccess: (_response, job) => {
      successMsg('Scheduled job run triggered');
      queryClient.invalidateQueries(['scheduledJobs']);
      queryClient.invalidateQueries(['scheduledJobRuns', job.id]);
      setJobToRun(null);
      setCurrentJob((previous) => previous ?? job);
    },
    onError: (error) => {
      errorMsg(error, 'Unable to run scheduled job');
    },
  });

  const deleteJobMutation = useMutation({
    mutationFn: (job: ScheduledJob) => deleteScheduledJob(job.id),
    onSuccess: (_response, job) => {
      successMsg('Scheduled job deleted successfully');
      queryClient.invalidateQueries(['scheduledJobs']);
      queryClient.removeQueries(['scheduledJobRuns', job.id]);
      setJobToDelete(null);

      if (currentJob?.id === job.id) {
        setCurrentJob(null);
        setDetailsOpen(false);
        setFormOpen(false);
      }
    },
    onError: (error) => {
      errorMsg(error, 'Unable to delete scheduled job');
    },
  });

  const jobs = useMemo(() => data?.data ?? [], [data?.data]);
  const totalJobs = data?.meta?.total ?? jobs.length;
  const denseHeight = table.dense ? 52 : 72;
  const isFiltered = isFiltersApplied(filters);
  const isNotFound = !jobs.length;

  useEffect(() => {
    if (!currentJob || formOpen) return;

    const refreshedJob = jobs.find((job) => job.id === currentJob.id);
    if (refreshedJob && refreshedJob !== currentJob) {
      setCurrentJob(refreshedJob);
    }
  }, [currentJob, formOpen, jobs]);

  const handleFilters = (
    name: keyof ScheduledJobListFilters,
    value: string
  ) => {
    setFilters((previous) => ({ ...previous, [name]: value }));
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(DEFAULT_FILTERS);
    table.setPage(0);
  };

  const handleCreateJob = () => {
    setDetailsOpen(false);
    setCurrentJob(null);
    setFormOpen(true);
  };

  const handleEditJob = (job: ScheduledJob) => {
    setDetailsOpen(false);
    setCurrentJob(job);
    setFormOpen(true);
  };

  const handleSelectJob = (job: ScheduledJob, shouldFocusRuns = false) => {
    setCurrentJob(job);
    setFocusRuns(shouldFocusRuns);
    setDetailsOpen(true);
  };

  const handleCloseForm = () => {
    setFormOpen(false);
    setCurrentJob(null);
  };

  const handleCloseDetails = () => {
    setDetailsOpen(false);
    setFocusRuns(false);
  };

  const handleSubmitJob = async (payload: ScheduledJobPayload) => {
    if (currentJob) {
      await updateJobMutation.mutateAsync({ id: currentJob.id, payload });
      return;
    }

    await createJobMutation.mutateAsync(payload);
  };

  const handleConfirmRun = () => {
    if (!jobToRun) return;
    runJobMutation.mutate(jobToRun);
  };

  const handleConfirmDelete = () => {
    if (!jobToDelete) return;
    deleteJobMutation.mutate(jobToDelete);
  };

  return (
    <Page title="Scheduled Jobs">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Scheduled Jobs"
          links={[
            { name: 'Dashboard', href: PATH_USER.root },
            { name: 'Scheduled Jobs' },
          ]}
          action={
            permissions.canCreate && (
              <Button
                variant="contained"
                onClick={handleCreateJob}
                startIcon={<Iconify icon="eva:plus-fill" />}
              >
                Add scheduled job
              </Button>
            )
          }
        />

        <Card>
          <ScheduledJobsTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            isFiltered={isFiltered}
          />

          {isLoading ? (
            <SkeletonLoadingTable />
          ) : (
            <>
              <Scrollbar>
                <TableContainer sx={{ minWidth: 1300 }}>
                  <Table size={table.dense ? 'small' : 'medium'}>
                    <TableHeadCustom headLabel={TABLE_HEAD} />
                    <TableBody>
                      {jobs.map((job) => (
                        <ScheduledJobsTableRow
                          key={job.id}
                          row={job}
                          permissions={permissions}
                          onSelectJob={() => handleSelectJob(job)}
                          onEditJob={() => handleEditJob(job)}
                          onRunJob={() => setJobToRun(job)}
                          onViewRuns={() => handleSelectJob(job, true)}
                          onDeleteJob={() => setJobToDelete(job)}
                          onToggleEnabled={(enabled) =>
                            toggleEnabledMutation.mutate({ job, enabled })
                          }
                          toggleDisabled={toggleEnabledMutation.isLoading}
                        />
                      ))}

                      <TableEmptyRows
                        height={denseHeight}
                        emptyRows={Math.max(0, table.rowsPerPage - jobs.length)}
                      />

                      <TableNoData
                        isNotFound={isNotFound}
                        title={
                          isFiltered
                            ? 'No scheduled jobs found'
                            : 'No scheduled jobs available'
                        }
                      />
                    </TableBody>
                  </Table>
                </TableContainer>
              </Scrollbar>

              <Box sx={{ position: 'relative' }}>
                {totalJobs > table.rowsPerPage && (
                  <TablePagination
                    rowsPerPageOptions={[10, 25, 50]}
                    component="div"
                    count={totalJobs}
                    rowsPerPage={table.rowsPerPage}
                    page={table.page}
                    onPageChange={table.onChangePage}
                    onRowsPerPageChange={table.onChangeRowsPerPage}
                  />
                )}

                <FormControlLabel
                  control={
                    <Switch
                      checked={table.dense}
                      onChange={table.onChangeDense}
                    />
                  }
                  label="Dense"
                  sx={{
                    px: 3,
                    py: 1.5,
                    top: 0,
                    position: { md: 'absolute' },
                  }}
                />
              </Box>
            </>
          )}
        </Card>
      </Container>

      <ScheduledJobFormDrawer
        open={formOpen}
        currentJob={currentJob}
        onClose={handleCloseForm}
        onSubmitJob={handleSubmitJob}
      />

      <ScheduledJobDetailsDrawer
        open={detailsOpen}
        job={currentJob}
        focusRuns={focusRuns}
        canRun={permissions.canExecute && !runJobMutation.isLoading}
        onRunJob={() => currentJob && setJobToRun(currentJob)}
        onClose={handleCloseDetails}
      />

      <DialogSimple
        dialogTitle="Run scheduled job now?"
        open={Boolean(jobToRun)}
        onClickButtonClose={() => setJobToRun(null)}
        loadingButton
        loadingButtonText="Run now"
        loadingButtonColor="warning"
        isSubmitting={runJobMutation.isLoading}
        onClickButtonLoading={handleConfirmRun}
      >
        <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2">
            This will manually trigger the selected scheduled job.
          </Typography>
          <Typography variant="subtitle2">{jobToRun?.name}</Typography>
        </Stack>
      </DialogSimple>

      <DialogSimple
        dialogTitle="Delete scheduled job?"
        open={Boolean(jobToDelete)}
        onClickButtonClose={() => setJobToDelete(null)}
        loadingButton
        loadingButtonText="Delete"
        loadingButtonColor="error"
        isSubmitting={deleteJobMutation.isLoading}
        onClickButtonLoading={handleConfirmDelete}
      >
        <Stack spacing={1.5} sx={{ px: 3, pb: 2 }}>
          <Typography variant="body2">
            This will permanently delete the selected scheduled job.
          </Typography>
          <Typography variant="subtitle2">{jobToDelete?.name}</Typography>
        </Stack>
      </DialogSimple>
    </Page>
  );
}
